import { useEffect, useRef, useState } from 'react';
import { Text, Transformer } from 'react-konva';
import { Html } from 'react-konva-utils';

const TextItem = ({
  item,
  selectedItemId,
  setSelectedItemId,
  handleItemUpdate,
  handleItemDelete,
  canvasType,
  canvasBounds,
  drawingMode = false,
  canvasRef,
}) => {
  const shapeRef = useRef(null);
  const transformerRef = useRef(null);
  const textareaRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [textValue, setTextValue] = useState(item.text || 'Text');
  const [textareaPosition, setTextareaPosition] = useState({ top: 0, left: 0 });
  const dragOffset = useRef({ x: 0, y: 0 });

  
  useEffect(() => {
    if (
      selectedItemId === item.id &&
      transformerRef.current &&
      shapeRef.current &&
      !drawingMode &&
      !item.locked &&
      !isEditing
    ) {
      transformerRef.current.nodes([shapeRef.current]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [selectedItemId, drawingMode, item.locked, isEditing]);

  
  useEffect(() => {
    if (selectedItemId !== item.id || drawingMode || isEditing) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        handleItemDelete(item.id, canvasType);
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        setIsEditing(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedItemId, drawingMode, isEditing, item.locked]);

  
  const handleMouseDown = (e) => {
    if (drawingMode || item.locked) return;

    e.cancelBubble = true;
    e.evt.stopPropagation();

    const stage = e.target.getStage();
    const pointerPos = stage.getPointerPosition();

    dragOffset.current = {
      x: pointerPos.x - item.position.x,
      y: pointerPos.y - item.position.y,
    };

    setIsDragging(true);
    setSelectedItemId(item.id);
    handleItemUpdate(item.id, { isSelected: true }, canvasType);
  };

    const handleMouseMove = (e) => {
    if (!isDragging || !shapeRef.current) return;

    const stage = shapeRef.current.getStage();
    const pointerPos = stage.getPointerPosition();
    if (!pointerPos) return;

    let newX = pointerPos.x - dragOffset.current.x;
    let newY = pointerPos.y - dragOffset.current.y;

    
    shapeRef.current.position({ x: newX, y: newY });

    
    const box = shapeRef.current.getClientRect();
    const { width: cW, height: cH } = canvasBounds;

    const margin = 25;
    const correctedPos = { x: newX, y: newY };

    if (box.x < -margin) correctedPos.x -= box.x + margin;
    if (box.y < -margin) correctedPos.y -= box.y + margin;
    if (box.x + box.width > cW + margin)
      correctedPos.x -= box.x + box.width - (cW + margin);
    if (box.y + box.height > cH + margin)
      correctedPos.y -= box.y + box.height - (cH + margin);

    shapeRef.current.position(correctedPos);
    shapeRef.current.getLayer().batchDraw();
  };

  
  const handleMouseUp = () => {
    if (isDragging && shapeRef.current) {
      const node = shapeRef.current;
      handleItemUpdate(
        item.id,
        {
          position: { x: node.x(), y: node.y() },
          isSelected: true,
        },
        canvasType
      );
    }
    setIsDragging(false);
  };

  
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  
  const handleTransformEnd = () => {
    if (drawingMode || !shapeRef.current || item.locked) return;

    const node = shapeRef.current;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    const newWidth = Math.max(5, item.width * scaleX);
    const newHeight = Math.max(5, item.height * scaleY);

    node.scaleX(1);
    node.scaleY(1);

    handleItemUpdate(
      item.id,
      {
        position: { x: node.x(), y: node.y() },
        width: newWidth,
        height: newHeight,
        rotate: node.rotation(),
      },
      canvasType
    );
  };

  
  const handleDblClick = () => {
    if (drawingMode || item.locked) return;
    setIsEditing(true);
  };

  


  
  const handleDblTap = () => {
    if (drawingMode || item.locked) return;
    setIsEditing(true);
  };

  
  const computeTextareaPosition = () => {
    if (!shapeRef.current) return { top: 0, left: 0, width: 200, height: 50 };

    const textNode = shapeRef.current;
    const stage = textNode.getStage();
    if (!stage) return { top: 0, left: 0, width: 200, height: 50 };

    
    const absolutePos = textNode.absolutePosition();

    
    const stageBox = stage.container().getBoundingClientRect();

    
    const scaleX = stage.scaleX();
    const scaleY = stage.scaleY();

    
    const domX = stageBox.left + absolutePos.x * scaleX;
    const domY = stageBox.top + absolutePos.y * scaleY;

    
    const width = (item.width || 200) * scaleX;
    const height = (item.height || 50) * scaleY;

    return {
      top: domY,
      left: domX,
      width: width,
      height: height,
    };
  };

  
  useEffect(() => {
    if (isEditing) {
      const position = computeTextareaPosition();
      setTextareaPosition(position);
    }
  }, [isEditing, item.position.x, item.position.y, item.width, item.height]);

  
  useEffect(() => {
    if (!isEditing || !shapeRef.current) return;

    const textNode = shapeRef.current;
    const stage = textNode.getStage();
    if (!stage) return;

    const updatePosition = () => {
      const position = computeTextareaPosition();
      setTextareaPosition(position);
    };

    
    stage.on('dragmove', updatePosition);
    stage.on('scale', updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      stage.off('dragmove', updatePosition);
      stage.off('scale', updatePosition);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [isEditing]);

  
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
     
      const len = textValue.length;
      textareaRef.current.setSelectionRange(len, len);
    }
  }, [isEditing, textValue]);

  // Handle text change
  const handleTextChange = (e) => {
    setTextValue(e.target.value);
  };

  // Handle text submit
  const handleTextSubmit = (newText) => {
    if (typeof newText !== 'string') {
      newText = textValue || '';
    }
    handleItemUpdate(item.id, { text: newText }, canvasType);
    setTextValue(newText);
    setIsEditing(false);
  };

  
  const handleTextCancel = () => {
    setIsEditing(false);
  };

  
  const handleTextareaBlur = (e) => {
    
    setTimeout(() => {

      if (isEditing && textareaRef.current && !textareaRef.current.contains(document.activeElement)) {
        handleTextSubmit(textValue);
      }
    }, 100);
  };

  return (
    <>
      <Text
        ref={shapeRef}
        name="text-item"
        text={textValue}
        x={item.position.x}
        y={item.position.y}
        width={item.width || 200}
        fontSize={item.fontSize || 16}
        fill={item.fill || '#000000'}
        rotation={item.rotate}
        draggable={false}
        onMouseDown={!drawingMode ? handleMouseDown : undefined}
        onDblClick={handleDblClick}
        onDblTap={handleDblTap}
        onTransformEnd={handleTransformEnd}
        visible={item.visible}
        listening={!drawingMode}
        perfectDrawEnabled={false}
        opacity={item.locked ? 0.7 : 1}
        align="left"
        verticalAlign="top"
        wrap="word"
        ellipsis={false}
      />
      {selectedItemId === item.id && !drawingMode && !item.locked && !isEditing && (
        <Transformer
          ref={transformerRef}
          rotateEnabled={true}
          enabledAnchors={[
            'top-left', 'top-center', 'top-right',
            'middle-left', 'middle-right',
            'bottom-left', 'bottom-center', 'bottom-right',
          ]}
          anchorSize={6}
          ignoreStroke={true}
          perfectDrawEnabled={false}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 1 || newBox.height < 1) {
              return oldBox;
            }
            return newBox;
          }}
        />
      )}
      {isEditing && (
        <Html
          groupProps={{
            x: item.position.x,
            y: item.position.y,
          }}
        >
          <textarea
            ref={textareaRef}
            value={textValue}
            onChange={handleTextChange}
            onBlur={handleTextareaBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                handleTextSubmit(textValue);
              }
              if (e.key === 'Escape') {
                handleTextCancel();
              }
            }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: item.width || 200,
              minHeight: item.height || 50,
              maxHeight: Math.max(200, (item.height || 50) * 10),
              fontSize: item.fontSize || 16,
              fontFamily: item.fontFamily || 'Arial, sans-serif',
              color: item.fill || '#000000',
              background: 'rgba(255, 255, 255, 0.95)',
              border: '2px solid #007bff',
              borderRadius: '4px',
              padding: '4px',
              resize: 'vertical', // Allow vertical resizing
              outline: 'none',
              zIndex: 1000,
              overflowY: 'auto', // Add vertical scroll when needed
              lineHeight: '1.2',
            }}
            autoFocus
          />
        </Html>
      )}
    </>
  );
};

export default TextItem;
